import { useState, useEffect, useRef, useId } from 'react';
import { Button } from '../../ui/button';
import type { InvestmentProperty } from '../InvestmentFlow.types';
import { formatCurrency } from '../InvestmentFlow.utils';

interface AmountStepProps {
  property: InvestmentProperty;
  investmentAmount: number;
  shares: number;
  projectedAnnualReturn: number;
  onAmountChange: (amount: number) => void;
  onContinue: () => void;
  canContinue: boolean;
  minInvestment: number;
  presetAmounts: readonly number[];
}

export function AmountStep({
  property,
  investmentAmount,
  shares,
  projectedAnnualReturn,
  onAmountChange,
  onContinue,
  canContinue,
  minInvestment,
  presetAmounts,
}: AmountStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const inputId = useId();
  const hintId = useId();

  // Local string state so the user can type freely (e.g. partially entered amounts)
  const [rawInput, setRawInput] = useState(investmentAmount > 0 ? String(investmentAmount) : '');

  // Focus heading on mount for screen reader announcement
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  function handleInputChange(value: string) {
    const digits = value.replace(/\D/g, '');
    setRawInput(digits);
    onAmountChange(digits ? parseInt(digits, 10) : 0);
  }

  function handlePresetSelect(preset: number) {
    setRawInput(String(preset));
    onAmountChange(preset);
  }

  return (
    <div className="space-y-8">
      {/* Property preview */}
      <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-card">
        <img
          src={property.image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{property.title}</p>
          <p className="text-sm text-muted-foreground truncate">{property.location}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
          >
            How much do you want to invest?
          </h1>
          <p id={hintId} className="text-lg text-muted-foreground">
            Minimum investment is {formatCurrency(minInvestment)}
          </p>
        </div>

        {/* Amount input */}
        <div className="relative">
          <label htmlFor={inputId} className="sr-only">
            Investment amount in dollars
          </label>
          <span
            aria-hidden="true"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl text-muted-foreground pointer-events-none"
          >
            $
          </span>
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            value={rawInput}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="0"
            aria-describedby={hintId}
            className="w-full pl-12 pr-4 py-6 text-4xl font-medium rounded-xl border-2 border-primary bg-background focus:outline-none focus:ring-4 focus:ring-primary/20 transition-all"
          />
        </div>

        {/* Preset amounts */}
        <div role="group" aria-label="Preset investment amounts" className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {presetAmounts.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              aria-pressed={investmentAmount === preset}
              className={`py-4 px-4 rounded-xl border-2 transition-all ${
                investmentAmount === preset
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-accent'
              }`}
            >
              <span className="text-lg font-medium">{formatCurrency(preset)}</span>
            </button>
          ))}
        </div>

        {/* Live stats — announced to screen readers when amount changes */}
        <div aria-live="polite" aria-atomic="true">
          {investmentAmount >= minInvestment && (
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-sm text-muted-foreground mb-1">You'll own</p>
                <p className="text-xl font-medium">{shares.toLocaleString()} shares</p>
              </div>
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-sm text-muted-foreground mb-1">Est. annual return</p>
                <p className="text-xl font-medium text-success">
                  ~{formatCurrency(projectedAnnualReturn)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        onClick={onContinue}
        disabled={!canContinue}
        className="w-full h-14 text-lg disabled:opacity-40"
      >
        Continue
      </Button>
    </div>
  );
}
